<template>
  <div ref="rootEl" class="ss" :class="{ 'ss--disabled': disabled }">
    <div class="ss__field">
      <input
        ref="inputEl"
        class="ss__input"
        type="text"
        role="combobox"
        autocomplete="off"
        aria-autocomplete="list"
        :aria-expanded="open"
        :aria-label="ariaLabel"
        :aria-activedescendant="activeId"
        :data-testid="inputTestid"
        :value="open ? query : selectedLabel"
        :placeholder="placeholder"
        :disabled="disabled"
        @focus="onFocus"
        @input="onInput"
        @blur="onBlur"
        @keydown="onKeydown"
      >
      <button
        v-if="modelValue && !disabled"
        type="button"
        class="ss__clear"
        :aria-label="t('vitrine.select.clear')"
        @mousedown.prevent
        @click="clear"
      >✕</button>
      <span class="ss__chevron" aria-hidden="true">
        <IconChevronDown :size="14" />
      </span>
    </div>

    <div v-if="open" class="ss__menu" role="listbox">
      <button
        v-for="(o, i) in filtered"
        :id="`${uid}-opt-${i}`"
        :key="o.value"
        type="button"
        class="ss__opt"
        :class="{ 'ss__opt--active': i === activeIndex, 'ss__opt--on': o.value === modelValue }"
        :data-value="o.value"
        role="option"
        :aria-selected="o.value === modelValue"
        @mousedown.prevent
        @mouseenter="activeIndex = i"
        @click="pick(o)"
      >{{ o.label }}</button>
      <div v-if="!filtered.length" class="ss__empty">{{ t('vitrine.select.noMatches') }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { IconChevronDown } from '@/components/ui/icons';

interface Option { value: string; label: string }

const props = withDefaults(defineProps<{
  modelValue: string;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  /** Forwarded to the text input so callers (and E2E) can target it. */
  inputTestid?: string;
}>(), { placeholder: '', disabled: false, ariaLabel: undefined, inputTestid: undefined });

const emit = defineEmits<{ (e: 'update:modelValue', v: string): void }>();

const { t } = useI18n();

const rootEl = ref<HTMLElement | null>(null);
const inputEl = ref<HTMLInputElement | null>(null);
const open = ref(false);
const query = ref('');
const activeIndex = ref(0);
const uid = `ss-${Math.random().toString(36).slice(2, 8)}`;

/** Diacritic-insensitive, case-insensitive normalisation for matching. */
function norm(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

const selectedLabel = computed(() => props.options.find(o => o.value === props.modelValue)?.label ?? '');

const filtered = computed<Option[]>(() => {
  const q = norm(query.value.trim());
  if (!q) return props.options;
  return props.options.filter(o => norm(o.label).includes(q));
});

const activeId = computed(() => (open.value && filtered.value.length ? `${uid}-opt-${activeIndex.value}` : undefined));

function openMenu() {
  if (props.disabled) return;
  open.value = true;
  query.value = '';
  activeIndex.value = 0;
}

function onFocus() { openMenu(); }

function onInput(e: Event) {
  open.value = true;
  query.value = (e.target as HTMLInputElement).value;
  activeIndex.value = 0;
}

function onBlur() {
  // mousedown.prevent on options keeps focus, so a blur means the user left the control → close.
  open.value = false;
  query.value = '';
}

function pick(o: Option) {
  emit('update:modelValue', o.value);
  open.value = false;
  query.value = '';
  inputEl.value?.blur();
}

function clear() {
  emit('update:modelValue', '');
  query.value = '';
  activeIndex.value = 0;
  nextTick(() => inputEl.value?.focus());
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    if (!open.value) { openMenu(); return; }
    activeIndex.value = Math.min(activeIndex.value + 1, filtered.value.length - 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    if (!open.value) { openMenu(); return; }
    activeIndex.value = Math.max(activeIndex.value - 1, 0);
  } else if (e.key === 'Enter') {
    if (open.value && filtered.value[activeIndex.value]) {
      e.preventDefault();
      pick(filtered.value[activeIndex.value]);
    }
  } else if (e.key === 'Escape') {
    if (open.value) {
      e.preventDefault();
      open.value = false;
      query.value = '';
    }
  }
}
</script>

<style scoped lang="scss">
.ss { position: relative; width: 100%; }

.ss__field {
  display: flex; align-items: center;
  border-radius: 10px;
  background: var(--bg-card); border: 1px solid var(--hairline-strong);
  transition: border-color var(--dur-fast, 0.15s);
}
.ss__field:focus-within { border-color: var(--gold); }
.ss--disabled .ss__field { opacity: 0.6; }

.ss__input {
  flex: 1; min-width: 0; box-sizing: border-box;
  padding: 10px 12px; background: transparent; border: none; outline: none;
  color: var(--text); font-size: 14px; font-family: inherit;
}
.ss__input::placeholder { color: var(--text-3); }

.ss__clear {
  flex: none; background: none; border: none; cursor: pointer;
  color: var(--text-3); font-size: 13px; line-height: 1; padding: 4px 2px;
}
.ss__chevron { flex: none; display: inline-flex; color: var(--text-3); padding: 0 10px 0 4px; }

.ss__menu {
  position: absolute; left: 0; right: 0; top: calc(100% + 6px); z-index: 41;
  border-radius: 14px; overflow: hidden; max-height: 214px; overflow-y: auto;
  background: var(--bg-card); border: 1px solid var(--hairline-strong);
  box-shadow: var(--shadow-pop, 0 16px 40px rgba(0, 0, 0, 0.18));
}
.ss__opt {
  display: block; width: 100%; text-align: left; cursor: pointer;
  padding: 10px 13px; background: none; border: none;
  color: var(--text); font-size: 14px; font-family: inherit;
}
.ss__opt--active { background: rgba(var(--accent-rgb, 138, 111, 58), 0.10); }
.ss__opt--on { color: var(--gold-light); font-weight: 600; }
.ss__empty { padding: 10px 13px; font-size: 13px; color: var(--text-3); }
</style>
